import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { AxiosError, AxiosResponse } from 'axios'
import {
  constructApiEndpoint,
  httpPost,
  httpPut,
  httpGet,
  httpDelete,
} from '@school/common/common-utils/helpers/httpClient'
import {
  SCHOOLLIST,
  ROLELIST,
  ADDUSER,
  ORGANIZATIONLIST,
  SCHOOLONE,
  ORGANIZATIONONE,
} from '@school/common/common-utils/constants/slice-constants'
import { toast } from 'react-toastify'
import { RootState } from '@school/store/store'
import {
  ErrorResponse,
  Organisation,
  Organization,
  Roles,
  School,
  User,
  UserAuth,
} from '@school/schemas/typeInterface'

interface UserResponse {
  message?: string[] | string
  user: User
}
//get user info by ID
export const getUserById = createAsyncThunk(
  'get/' + ADDUSER,
  async (payload: { id?: string }, { rejectWithValue }) => {
    try {
      const response: AxiosResponse<UserAuth> = await httpGet(ADDUSER + '/' + payload.id)

      return response.data
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const errorData = error.response?.data as ErrorResponse

        const message = Array.isArray(errorData?.message)
          ? errorData.message[0]
          : errorData?.message || error.message || 'Failed to fetch user'

        return rejectWithValue(message)
      }

      return rejectWithValue('An unexpected error occurred')
    }
  },
)

export const getSchoolOne = createAsyncThunk(
  SCHOOLONE,
  async (payload: { id?: string }, { rejectWithValue }) => {
    try {
      const response: AxiosResponse<School> = await httpGet(SCHOOLONE + '/' + payload.id)

      return response.data
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const errorData = error.response?.data as ErrorResponse

        const message = Array.isArray(errorData?.message)
          ? errorData.message[0]
          : errorData?.message || error.message || 'Failed to fetch user'

        return rejectWithValue(message)
      }

      return rejectWithValue('An unexpected error occurred')
    }
  },
)
//get organizationbyId
export const getOrganizationOne = createAsyncThunk(
  ORGANIZATIONONE,
  async (payload: { id?: string }, { rejectWithValue }) => {
    try {
      const response: AxiosResponse<Organisation> = await httpGet(
        ORGANIZATIONONE + '/' + payload.id,
      )

      return response.data
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const errorData = error.response?.data as ErrorResponse

        const message = Array.isArray(errorData?.message)
          ? errorData.message[0]
          : errorData?.message || error.message || 'Failed to fetch user'

        return rejectWithValue(message)
      }

      return rejectWithValue('An unexpected error occurred')
    }
  },
)

// Async thunk for check user exists
export const schoolList = createAsyncThunk(
  SCHOOLLIST,
  async (id: string | undefined, { rejectWithValue }) => {
    try {
      let url = SCHOOLLIST
      if (id) url += '?id=' + id

      const endpoint = constructApiEndpoint(url, {})
      const response: AxiosResponse<School[]> = await httpGet(endpoint)
      return response.data
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const errorData = error.response?.data as ErrorResponse
        const message = errorData?.message?.[0] || error.message || 'Failed to fetch schools'
        return rejectWithValue(message)
      }
      return rejectWithValue('An unexpected error occurred')
    }
  },
)

//role list
export const roleList = createAsyncThunk(ROLELIST, async (_, { rejectWithValue }) => {
  try {
    const endpoint = constructApiEndpoint(ROLELIST, {})
    const response: AxiosResponse<Roles[]> = await httpGet(endpoint)
    return response?.data
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const errorData = error.response?.data as ErrorResponse
      const message = errorData?.message?.[0] || error.message || 'Failed to fetch role'
      return rejectWithValue(message)
    }
    return rejectWithValue('An unexpected error occurred')
  }
})
//organizationList
export const organizationList = createAsyncThunk(
  ORGANIZATIONLIST,
  async (_, { rejectWithValue }) => {
    try {
      const endpoint = constructApiEndpoint(ORGANIZATIONLIST, {})
      const response: AxiosResponse<Organization[]> = await httpGet(endpoint)
      return response.data
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const errorData = error.response?.data as ErrorResponse
        const message = errorData?.message?.[0] || error.message || 'Failed to fetch organization'
        return rejectWithValue(message)
      }
      return rejectWithValue('An unexpected error occurred')
    }
  },
)

// Async thunk for adding or updating a user
export const addOrUpdateUser = createAsyncThunk(
  ADDUSER,
  async ({ id, payload }: { id?: string; payload: any }, { rejectWithValue }) => {
    try {
      const isUpdate = !!id
      let url = ADDUSER
      if (isUpdate) url += '/' + id
      const endpoint = constructApiEndpoint(url, {})

      // Make the API request with appropriate method
      const response: AxiosResponse<UserResponse> = isUpdate
        ? await httpPut(endpoint, payload)
        : await httpPost(endpoint, payload)

      // Safely extract the message
      const responseData = response.data
      const message = Array.isArray(responseData.message)
        ? responseData.message[0]
        : responseData.message || 'User updated'

      toast.success(message)
      // Return or use cleaned values
      return responseData
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const errorData = error.response?.data as ErrorResponse
        const message = Array.isArray(errorData?.message)
          ? errorData.message[0]
          : errorData?.message || error.message || 'User save failed'
        return rejectWithValue(message)
      }
      return rejectWithValue('An unexpected error occurred')
    }
  },
)

//delete user
export const deleteUser = createAsyncThunk(
  'delete/' + ADDUSER,
  async ({ id }: { id?: string | number }, { rejectWithValue }) => {
    try {
      const url = ADDUSER + '/' + id
      const endpoint = constructApiEndpoint(url, {})
      const response: AxiosResponse<UserResponse> = await httpDelete(endpoint)
      // Safely extract the message
      const responseData = response.data
      const message = Array.isArray(responseData.message)
        ? responseData.message[0]
        : responseData.message || 'User Deleted'

      toast.success(message)

      return responseData
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const errorData = error.response?.data as ErrorResponse
        const message = Array.isArray(errorData?.message)
          ? errorData.message[0]
          : errorData?.message || error.message || 'User delete failed'
        return rejectWithValue(message)
      }
      return rejectWithValue('An unexpected error occurred')
    }
  },
)

interface intialStateProps {
  school: School[]
  schoolone: School | null
  organizationone: Organisation | null
  roles: Roles[]
  userone: UserAuth | null
  organization: Organization[]
  loading: boolean
  error: string | null
}
const initialState: intialStateProps = {
  school: [],
  schoolone: null,
  organizationone: null,
  roles: [],
  userone: null,
  organization: [],
  loading: false,
  error: '',
}

// Create slice
const UserSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch address list
      .addCase(schoolList.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(schoolList.fulfilled, (state, action) => {
        state.loading = false
        state.school = action.payload
      })
      .addCase(schoolList.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(roleList.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(roleList.fulfilled, (state, action) => {
        state.loading = false
        state.roles = action.payload
      })
      .addCase(roleList.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(getUserById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getUserById.fulfilled, (state, action) => {
        state.loading = false
        state.userone = action.payload
      })
      .addCase(getUserById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      //getOrganizationOne
      .addCase(getOrganizationOne.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getOrganizationOne.fulfilled, (state, action) => {
        state.loading = false
        state.organizationone = action.payload
      })
      .addCase(getOrganizationOne.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      //schoolOne
      .addCase(getSchoolOne.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getSchoolOne.fulfilled, (state, action) => {
        state.loading = false
        state.schoolone = action.payload
      })
      .addCase(getSchoolOne.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(deleteUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteUser.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(organizationList.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(organizationList.fulfilled, (state, action) => {
        state.loading = false
        state.organization = action.payload
      })
      .addCase(organizationList.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})
// Selectors
export const selectUserById = (state: RootState) => state.user.userone

export const selectSchoolById = (state: RootState) => state.user.schoolone
export const selectOrganizationById = (state: RootState) => state.user.organizationone
export const selectSchool = (state: RootState) => state.user.school
export const selectRole = (state: RootState) => state.user.roles
export const selectOrganization = (state: RootState) => state.user.organization
export default UserSlice.reducer
